import PropTypes from 'prop-types';

import { useForm, useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useEffect, useMemo, useState } from 'react';
import { liquidationSchema } from 'features/SubmitProjectProposal/validation';
import {
  postLiquidation,
  getLiquidationById,
  uploadLiquidationFile,
  deleteLiquidationFile,
} from 'apis/liquidation.api';
import { getProjectById } from 'apis/projects.api';

import { COUNCIL_STATUS, TOAST } from 'constant';
import { toast } from 'layout';
import LiquidationForm from 'features/SubmitProjectProposal/components/LiquidationForm';
import { mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(liquidationSchema) };

function LiquidationAgreement({ readOnly }) {
  // #region Data
  const { t } = useTranslation();
  const { projectId } = useParams();

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    mode: 'onChange',
    ...formOptions,
    shouldFocusError: true,
  });
  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });

  const [appendixFilesDelete, setAppendixFilesDelete] = useState([]);

  const { data: _data, refetch } = useQuery({
    queryKey: ['liquidation', projectId],
    queryFn: () => getLiquidationById(projectId),
  });

  const liquidation = useMemo(() => _data?.data ?? null, [_data]);

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectById(projectId),
  });

  const liquidationStatus = useMemo(
    () => projectData?.data?.acceptanceCouncilStatus ?? null,
    [projectData]
  );

  const { mutate: postLiquidationMutate } = useMutation(postLiquidation);
  const { mutate: uploadLiquidationFileMutate } = useMutation(uploadLiquidationFile);

  const { mutate: deleteLiquidationFileMutate } = useMutation(deleteLiquidationFile);

  const onSubmit = (data) => {
    const { isCompleted, appendixFiles, projectAppendixDeletedFileIds, ...rest } = data;
    const dataSender = { projectId, ...rest, isCompleted: isCompleted === 'Đạt' };
    postLiquidationMutate(dataSender, {
      onSuccess: () => {
        const liquidationFiles = new FormData();
        liquidationFiles.append('projectId', projectId);
        liquidationFiles.append('liquidationDecisionFile', data?.liquidationDecisionFile);
        appendixFiles?.forEach((file) => {
          liquidationFiles.append('appendixFiles', file);
        });
        uploadLiquidationFileMutate(liquidationFiles, {
          onSuccess: () => {
            if (projectAppendixDeletedFileIds?.length > 0) {
              deleteLiquidationFileMutate(
                {
                  projectId,
                  liquidationAppendixIds: projectAppendixDeletedFileIds,
                },
                {
                  onSuccess: () => {
                    setAppendixFilesDelete([]);
                    toast(TOAST.SUCCESS, t('liquidation.saveSuccess'));
                    refetch();
                    reset({ appendixFiles: [] });
                  },
                }
              );
            } else {
              toast(TOAST.SUCCESS, t('liquidation.saveSuccess'));
              refetch();
              reset({ appendixFiles: [] });
            }
          },
        });
      },
      onError: (err) => {
        toast(TOAST.ERROR, mapError(t, err));
      },
    });
  };

  useEffect(() => {
    if (liquidation) {
      setValue('vnuExpenditure', liquidation?.vnuExpenditure || 0);
      setValue('suggestedExpenditure', liquidation?.suggestedExpenditure || 0);
      setValue('acceptedExpenditure', liquidation?.acceptedExpenditure || 0);
      setValue('stateReimbursementExpenditure', liquidation?.stateReimbursementExpenditure || 0);
      setValue('liquidationDate', new Date(liquidation?.liquidationDate));
      setValue('code', liquidation?.code);
    }
  }, [liquidation]);

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />

      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  return (
    <div className="tab-view-create-contract">
      <LiquidationForm
        control={control}
        errors={errors}
        handleSubmit={handleSubmit(onSubmit)}
        watch={watch}
        refetch={refetch}
        setValue={setValue}
        checkKeyDown={checkKeyDown}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        footerConfirmationPrompt={footerConfirmationPrompt}
        liquidation={liquidation}
        appendixFilesDelete={appendixFilesDelete}
        setAppendixFilesDelete={setAppendixFilesDelete}
        isCompleted={liquidationStatus === COUNCIL_STATUS.QUALIFIED}
        readOnly={readOnly}
      />
    </div>
  );
}

LiquidationAgreement.propTypes = {
  readOnly: PropTypes.bool,
};

LiquidationAgreement.defaultProps = { readOnly: false };

export default LiquidationAgreement;
